import React, {ReactElement, useEffect, useState} from "react";
import {FileUploadModalProps} from "./FileUploadModalProps";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {
    AcquisitionPropertyTemplateName,
    useGenerateAcquisitionExternalCoaFileUploadUrlMutation,
    useGetChooseExternalCoaDataQuery,
    useGetExternalCoaPropertyDataLazyQuery,
    useProcessAcquisitionExternalCoaFileMutation
} from "../../../../__generated__/generated_types";
import {Field, Label, Message} from "@zendeskgarden/react-forms";
import {CustomZDDropdown} from "../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {Button} from "@zendeskgarden/react-buttons";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../constants/Colors";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";
import * as css from "./styles/chooseExternalCoa.module.scss";
import {useFetchTemplateCsv} from "../../logic/useFetchTemplateCsv";
import {UpdateCustomCoAValuesButton} from "./csvbox/UpdateCustomCoAValuesButton";
import {CustomCoADataItem} from "./csvbox/custom-coa-common";

export function ChooseExternalCoa(props: FileUploadModalProps): ReactElement {
    const {data, loading} = useGetChooseExternalCoaDataQuery({
        fetchPolicy: "no-cache",
        variables: {
            propertyId: props.propertyId
        }
    });

    const [getExternalCoaPropertyData, {data: externalCoaPropertyData, loading: externalCoaPropertyDataLoading}]=
        useGetExternalCoaPropertyDataLazyQuery({
            fetchPolicy: "no-cache"
        });

    const [generateUrl, {loading: loadingUrl}] = useGenerateAcquisitionExternalCoaFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });

    const [processVals, {loading: processValsLoading}] = useProcessAcquisitionExternalCoaFileMutation({
        notifyOnNetworkStatusChange: true
    });

    const {fetchTemplate} = useFetchTemplateCsv("custom-coa");

    const [selectedCoa, setSelectedCoa] = useState<string>("");
    const [hasMapping, setHasMapping] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [savedToDatabaseTime, setSavedToDatabaseTime] = useState<string | Date | null>(null);
    const [coaData, setCoaData] = useState<CustomCoADataItem[] | undefined>(undefined);
    const [coaFileName, setCoaFileName] = useState<string | undefined>(undefined);

    useEffect(() => {
        if(!loading && data?.getAcquisitionProperty) {
            setSelectedCoa(data.getAcquisitionProperty.state.externalCoaId ?? "");
            setHasMapping(data.getAcquisitionProperty.state.externalCoaHasMapping === true);
            setSavedToDatabaseTime(data.getAcquisitionProperty.state.accountValuesSavedToDatabase);
        }
    }, [data, loading]);

    useEffect(() => {
        if(loading || !data || !selectedCoa) {
            setSavedToDatabaseTime(null);
            return;
        }

        if(data.getAcquisitionProperty?.state.externalCoaId === selectedCoa) {
            setSavedToDatabaseTime(data.getAcquisitionProperty.state.accountValuesSavedToDatabase ?? null);
        } else {
            setSavedToDatabaseTime(null);
        }
    }, [data, loading, selectedCoa]);

    useEffect(() => {
        if(!selectedCoa) {
            return;
        }

        getExternalCoaPropertyData({
            variables: {
                propertyId: props.propertyId,
                externalCoaId: selectedCoa
            }
        });
    }, [selectedCoa]);

    useEffect(() => {
        if(!externalCoaPropertyDataLoading && externalCoaPropertyData) {
            setHasMapping(externalCoaPropertyData.getExternalChartOfAccountsPropertyData.hasMapping);
        }
    }, [externalCoaPropertyData, externalCoaPropertyDataLoading]);

    function isSubmitDisabled(): boolean {
        return !selectedCoa || !hasMapping || (!savedToDatabaseTime && !coaData) || externalCoaPropertyDataLoading || loadingUrl || processValsLoading || isSubmitting;
    }

    function handleSubmitWithFile(): void {
        if(!coaData || !selectedCoa) {
            return;
        }

        const encoder = new TextEncoder();
        const valuesFile = encoder.encode(JSON.stringify(coaData));

        setIsSubmitting(true);
        let key: string | null = null;
        generateUrl()
            .then(res => {
                if(res.data?.generateExternalChartOfAccountsFileUploadUrl) {
                    key = res.data.generateExternalChartOfAccountsFileUploadUrl.key;
                    const coaUrl = res.data.generateExternalChartOfAccountsFileUploadUrl.signedUrl;

                    // Upload the file.
                    return fetch(coaUrl, {
                        method: 'PUT',
                        body: valuesFile.buffer,
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    });
                }
                throw new Error("Unable to upload file.");
            })
            .then(() => {
                if(!key) {
                    throw new Error("Unable to upload file.");
                }

                return processVals({
                    variables: {
                        propertyId: props.propertyId,
                        externalCoaId: selectedCoa,
                        fileKey: key
                    }
                });
            })
            .then(jobRes => {
                if(jobRes.data?.processAcquisitionExternalCoaFile) {
                    props.jobCreated(jobRes.data.processAcquisitionExternalCoaFile.id, props.propertyId);
                    toast.success("Values uploaded.");
                    props.onModalClose();
                }
            })
            .catch(() => {
                toast.error("Error uploading file.");
                setIsSubmitting(false);
            });
    }

    function handleSubmitWithoutFile(): void {
        if(!selectedCoa) {
            return;
        }

        setIsSubmitting(true);
        processVals({
            variables: {
                propertyId: props.propertyId,
                externalCoaId: selectedCoa
            }
        })
            .then(jobRes => {
                if(jobRes.data?.processAcquisitionExternalCoaFile) {
                    props.jobCreated(jobRes.data.processAcquisitionExternalCoaFile.id, props.propertyId);
                    toast.success("Values processing.");
                    props.onModalClose();
                }
            })
            .catch(() => {
                toast.error("Error processing values.");
                setIsSubmitting(false);
            });
    }

    function handleSubmit(): void {
        if(coaData !== undefined) {
            handleSubmitWithFile();
        } else {
            handleSubmitWithoutFile();
        }
    }

    function reset(): void {
        setSelectedCoa("");
        setCoaData(undefined);
        setCoaFileName(undefined);
        setHasMapping(false);
        props.onModalClose();
    }

    function recheckMapping(): void {
        getExternalCoaPropertyData({
            variables: {
                propertyId: props.propertyId,
                externalCoaId: selectedCoa
            }
        });
    }

    function renderDate(date: unknown): string {
        if(typeof date === "string") {
            try {
                const d = new Date(date);
                return d.toLocaleString();
            } catch(e) {
                return date;
            }
        } else if(date instanceof Date) {
            return date.toLocaleString();
        }
        return "";
    }

    return <>
        <Header>Choose External Chart of Accounts</Header>
        <Body className={css.modalBody}>
            <form>
                <Field className={css.field}>
                    <Label className={css.spaceLabel}>External Chart of Accounts</Label>
                    <CustomZDDropdown
                        initialSelectedItems={selectedCoa !== "" ? [selectedCoa] : []}
                        applySelectedItems={items => {
                            if(Array.isArray(items) && items.length === 1) {
                                setSelectedCoa(items[0]);
                            }
                        }}
                        openDropdownPlaceholder={"Chart of Accounts"}
                        closedDropdownPlaceholder={"Chart of Accounts"}
                        options={!loading && data?.listExternalChartOfAccounts ?
                                data.listExternalChartOfAccounts.map(coa => ({
                                    value: coa.id,
                                    label: coa.name
                                })):
                                []
                        }
                        isError={false}
                    />
                </Field>
                <div className={css.field}>
                    <h5 style={{fontWeight: "bold"}}>Chart of Accounts File with Values {!loading && !savedToDatabaseTime && <span className={css.required}>(required)</span>}</h5>
                    <UpdateCustomCoAValuesButton
                        externalCoaId={selectedCoa}
                        data={(filename, uploaded) => {
                            setCoaData(uploaded);
                            setCoaFileName(filename);
                        }}
                    />
                    {coaData &&
                        <div style={{fontSize: "0.9rem", marginTop: "0.25rem"}}>
                            {coaFileName}
                            <Message style={{marginLeft: "0.25rem"}}>You can change the file by clicking the button above.</Message>
                        </div>
                    }
                    {!loading &&
                        <div style={{fontSize: "0.9rem", marginTop: "0.4rem"}}>Last Values Data: {savedToDatabaseTime ? renderDate(savedToDatabaseTime) : "N/A"}</div>
                    }
                    <div style={{marginTop: "0.5rem"}}><Button isLink onClick={() => fetchTemplate(props.propertyId, AcquisitionPropertyTemplateName.ExternalCoa)}>Download Custom CoA Template</Button></div>
                </div>
                {selectedCoa &&
                    <div className={`${css.field} ${css.coaMap}`}>
                        {externalCoaPropertyDataLoading ?
                            <>Checking...</>:
                            <>
                                {hasMapping ?
                                    <div className={css.coaMapHeader}>Optionally Update Chart of Accounts Mapping</div>:
                                    <div className={css.coaMapHeader}>Please Map the Chart of Accounts <span className={css.required}>(required)</span></div>
                                }
                                <div>
                                    {data?.getAcquisitionProperty?.coaId ?
                                        <Link target="_blank" to={`/external-coa/${selectedCoa}/mapping/${data.getAcquisitionProperty.coaId}`}>Edit Coa Mapping</Link> :
                                        <div>Your property is not attached to any of your internal chart of accounts.</div>
                                    }
                                </div>
                                {!hasMapping &&
                                    <div>
                                        <div className={css.coaMapRecheck}>When you have mapped your chart of accounts, come back here and click the <span style={{fontWeight: 600}}>"Recheck Mapping"</span> button.</div>
                                        <Button className={css.recheckButton} isNeutral size="small" onClick={() => recheckMapping()}>Recheck Mapping</Button>
                                    </div>
                                }
                            </>
                        }
                    </div>
                }
            </form>
        </Body>
        <Footer>
            <FooterItem>
                <Button onClick={reset} isBasic>
                    Cancel
                </Button>
            </FooterItem>
            <FooterItem>
                <Button isPrimary onClick={handleSubmit} disabled={isSubmitDisabled()}>
                    {
                        isSubmitting
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                : <span>Process</span>
                    }
                </Button>
            </FooterItem>
        </Footer>
    </>;
}